/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import axios, {
  AxiosAdapter,
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios"
import {
  ApiRequestOptions,
  ApiResponsePayload,
} from "../../interfaces/electron"

export function isElectronApiAvailable(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.electron?.apiRequest === "function"
  )
}

export function createElectronIpcAdapter(
  axiosInstance: AxiosInstance
): AxiosAdapter {
  const existingAdapter = axiosInstance.defaults.adapter
  const fallbackAdapter =
    existingAdapter &&
    typeof existingAdapter === "function" &&
    existingAdapter.name !== "electronIpcAdapter"
      ? existingAdapter
      : axios.getAdapter(axios.defaults.adapter || "xhr")

  return async function electronIpcAdapter(
    config: InternalAxiosRequestConfig
  ): Promise<AxiosResponse> {
    if (!isElectronApiAvailable()) {
      const adapterToUse =
        typeof fallbackAdapter === "function"
          ? fallbackAdapter
          : axios.getAdapter("xhr")
      return adapterToUse(config)
    }

    if (config.signal?.aborted) {
      throw new AxiosError("Request canceled", AxiosError.ERR_CANCELED, config)
    }

    // Resolve full URL including baseURL and serialized params
    let fullUrl = axiosInstance.getUri(config)
    if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
      const defaultBaseUrl =
        process.env.NEXT_PUBLIC_SALES_API ||
        process.env.VITE_SALES_API ||
        "http://localhost:8101"
      fullUrl = `${defaultBaseUrl.replace(/\/+$/, "")}/${fullUrl.replace(/^\/+/, "")}`
    }

    // Serialize headers
    const headers: Record<string, string> = {}
    if (config.headers) {
      if (typeof (config.headers as any).toJSON === "function") {
        const json = (config.headers as any).toJSON()
        for (const [key, value] of Object.entries(json)) {
          if (value !== undefined && value !== null) {
            headers[key] = String(value)
          }
        }
      } else {
        for (const [key, value] of Object.entries(config.headers)) {
          if (value !== undefined && value !== null) {
            headers[key] = String(value)
          }
        }
      }
    }

    const requestOptions: ApiRequestOptions = {
      method: (config.method || "get").toUpperCase(),
      url: fullUrl,
      headers,
      data: config.data,
      responseType: config.responseType,
      timeout: config.timeout,
    }

    let ipcResponse: ApiResponsePayload
    try {
      ipcResponse = await window.electron!.apiRequest(requestOptions)
    } catch (ipcErr: any) {
      throw new AxiosError(
        ipcErr.message || "Electron IPC request failed",
        AxiosError.ERR_NETWORK,
        config
      )
    }

    if (config.signal?.aborted) {
      throw new AxiosError("Request canceled", AxiosError.ERR_CANCELED, config)
    }

    const responseHeaders = new AxiosHeaders(ipcResponse.headers || {})
    const response: AxiosResponse = {
      data: ipcResponse.data,
      status: ipcResponse.status,
      statusText: ipcResponse.statusText || "",
      headers: responseHeaders,
      config,
      request: null,
    }

    if (ipcResponse.status === 0) {
      throw new AxiosError(
        ipcResponse.statusText || "Network Error",
        AxiosError.ERR_NETWORK,
        config,
        null,
        response
      )
    }

    const validateStatus = config.validateStatus
    const isValid =
      !ipcResponse.status ||
      !validateStatus ||
      validateStatus(ipcResponse.status)

    if (isValid) {
      return response
    }

    const code =
      ipcResponse.status >= 400 && ipcResponse.status < 500
        ? AxiosError.ERR_BAD_REQUEST
        : AxiosError.ERR_BAD_RESPONSE

    throw new AxiosError(
      `Request failed with status code ${ipcResponse.status}`,
      code,
      config,
      null,
      response
    )
  }
}
